import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { decryptData } from "../../utils/interceptors/localStorageService";
import { jwtDecode } from "jwt-decode";
import { loadPermissionFlags, PERMISSION_FLAGS_STORAGE_KEY, readPermissionFlagsFromCache } from "../../utils/helper/permission-toggle";
import { nowMs } from "../../utils/helper/util";

const PermissionContext = createContext(null);
const LOCALSTORAGE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
export const usePermissions = () => {
  const context = useContext(PermissionContext);

  if (!context) {
    throw new Error("usePermissions must be used within PermissionProvider");
  }
  return context;
};

export const PermissionProvider = ({ children }) => {
  const [permissionsCache, setPermissionsCache] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Initial load on mount
   */


  /**
   * Watch for permission changes across browser tabs or components
   */
  useEffect(() => {
    const onStorageChange = (e) => {
      // Permission cache updated in another tab
      if (e.key === PERMISSION_FLAGS_STORAGE_KEY && e.newValue) {
        try {
          const data = JSON.parse(e.newValue).value || [];
          setPermissionsCache(data);
          console.log("🔄 Permissions synced from another tab");
        } catch (err) {
          console.error("Failed to parse permission storage event:", err);
        }
      }

      // Session data changed (login/logout)
      if (e.key === "sessionData") {
        console.log("🔄 Session changed, reloading permissions");
        loadPermissionFlags();
      }
    };

    window.addEventListener("storage", onStorageChange);
    return () => window.removeEventListener("storage", onStorageChange);
  }, [loadPermissionFlags]);

  /**
   * Check if user has a specific permission (and it's enabled)
   * @param {string} permissionCode - e.g., "bnk_view_policies"
   * @returns {boolean}
   */
  const hasPermission = useCallback(
    (permissionCode) => {

      return true;

      // if (!Array.isArray(readPermissionFlagsFromCache()) || readPermissionFlagsFromCache().length === 0) {
      //   return false;
      // }
      // const match = readPermissionFlagsFromCache().find(
      //   (p) => p?.permissionCode === permissionCode
      // );

      // return Boolean(match?.enabled);
    },
    [readPermissionFlagsFromCache]
  );

  /**
   * Find a permission object by code (returns full object)
   * @param {string} permissionCode - e.g., "bnk_view_policies"
   * @returns {Object|null}
   */
  const findPermission = useCallback(
    (permissionCode) => {
      if (!Array.isArray(permissionsCache)) {
        return null;
      }

      return (
        permissionsCache.find((p) => p?.permissionCode === permissionCode) ||
        null
      );
    },
    [permissionsCache]
  );

  /**
   * Check if user has ANY of the provided permissions
   * @param {string[]} permissionCodes
   * @returns {boolean}
   */
  const hasAnyPermission = useCallback(
    (permissionCodes) => {
      if (!Array.isArray(permissionCodes)) {
        return false;
      }
      return permissionCodes.some((code) => hasPermission(code));
    },
    [hasPermission]
  );

  /**
   * Check if user has ALL of the provided permissions
   * @param {string[]} permissionCodes
   * @returns {boolean}
   */
  const hasAllPermissions = useCallback(
    (permissionCodes) => {
      if (!Array.isArray(permissionCodes)) {
        return false;
      }
      return permissionCodes.every((code) => hasPermission(code));
    },
    [hasPermission]
  );

  /**
   * Get all enabled permissions
   * @returns {Array}
   */
  const getEnabledPermissions = useCallback(() => {
    return permissionsCache.filter((p) => p?.enabled === true);
  }, [permissionsCache]);

  /**
   * Get permissions by module type
   * @param {number} moduleType - e.g., 0 for Loans, 2 for Deposits
   * @returns {Array}
   */
  const getPermissionsByModule = useCallback(
    (moduleType) => {
      return permissionsCache.filter(
        (p) => p?.moduleType === moduleType && p?.enabled === true
      );
    },
    [permissionsCache]
  );

  /**
   * Clear all permissions (useful for logout)
   */
  const clearPermissions = useCallback(() => {
    setPermissionsCache([]);
    localStorage.removeItem(PERMISSION_FLAGS_STORAGE_KEY);
    console.log("🗑️ Permissions cleared");
  }, []);

  const value = {
    // State
    permissionsCache,
    isLoading,

    // Core methods
    hasPermission,
    findPermission,
    hasAnyPermission,
    hasAllPermissions,

    // Utility methods
    getEnabledPermissions,
    getPermissionsByModule,
    updatePermissions: loadPermissionFlags,
    clearPermissions,

    // Legacy support (if needed elsewhere in your codebase)
    setPermissionsCache,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};